<!-- src/pages/MyPage.vue -->
<script setup>
import { addContentLogAPI } from '@/api/logServiceApi';
import { MyInquiryList, NmcBoardView, NmcBoardWrite } from '@/components/board';
import IconEmail from '@/components/svg/IconEmail.vue';
import IconHp from '@/components/svg/IconHp.vue';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'mypage' } });

const creditInfoRef = ref(null);

const route = useRoute();
const router = useRouter();
const store = useStore();
const myInfoStore = useMyInfoStore();

const boardId = ref(2336); // 기본 문의내역 ID
const board = ref('inquiry'); // 기본 보드 이름

const boardMapping = {
  counseling: 2508,
  inquiry: 2336,
  eventinquiry: 2522,
};

const contact = {
  email: 'info@dangamsoft.com',
  mobile: '070-5165-3635',
};

const List = (boardName) => {
  if (boardName === 'inquiry') addContentLogAPI('UM_inquiry_list');
  else addContentLogAPI('UM_counseling_list');
  board.value = boardName; // 전달된 boardName으로 board 값을 설정
  boardId.value = boardMapping[boardName] || boardId.value; // 매핑된 ID로 설정, 없으면 기존 ID 유지
};

/** 운영시간 안내 토글 상태 */
const isNoticeExpanded = ref(false);

/** 운영시간 안내 토글 */
const toggleNotice = () => {
  isNoticeExpanded.value = !isNoticeExpanded.value;
};

/** 아코디언 트랜지션 - 실제 높이 기반으로 부드럽게 펼치기/접기 */
const onEnter = (el, done) => {
  el.style.overflow = 'hidden';
  el.style.maxHeight = '0';
  el.style.paddingTop = '0';
  el.style.paddingBottom = '0';
  el.style.opacity = '0';
  void el.offsetHeight;
  el.style.transition = 'max-height 0.3s ease, padding 0.3s ease, opacity 0.3s ease';
  el.style.maxHeight = el.scrollHeight + 'px';
  el.style.paddingTop = '';
  el.style.paddingBottom = '';
  el.style.opacity = '1';
  el.addEventListener('transitionend', done, { once: true });
};

const onAfterEnter = (el) => {
  el.style.maxHeight = '';
  el.style.overflow = '';
  el.style.transition = '';
};

const onLeave = (el, done) => {
  el.style.overflow = 'hidden';
  el.style.maxHeight = el.scrollHeight + 'px';
  el.style.opacity = '1';
  void el.offsetHeight;
  el.style.transition = 'max-height 0.3s ease, padding 0.3s ease, opacity 0.3s ease';
  el.style.maxHeight = '0';
  el.style.paddingTop = '0';
  el.style.paddingBottom = '0';
  el.style.opacity = '0';
  el.addEventListener('transitionend', done, { once: true });
};

const onAfterLeave = (el) => {
  el.style.maxHeight = '';
  el.style.overflow = '';
  el.style.transition = '';
  el.style.paddingTop = '';
  el.style.paddingBottom = '';
};

onMounted(() => {
  // 문의 내역 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('MY_inquiry_list_view');
  if (route?.query?.boardName) {
    board.value = route.query.boardName;
    boardId.value = boardMapping[board.value] || boardId.value;
  }
});

/** 뒤로가기: 항상 메인으로 */
const handleBack = () => {
  router.replace('/main');
};

const isWebView = ref(false);
onMounted(() => {
  isWebView.value = !!window.ReactNativeWebView;
});

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);
const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};
const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};
watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
  },
);

const isLoggedIn = computed(() => {
  const uid = store.getters['user/id'];
  const uname = store.getters['user/name'];
  try {
    const u = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(uid || uname || u?.name);
  } catch {
    return !!(uid || uname);
  }
});
const displayName = computed(() =>
  isLoggedIn.value ? myInfoStore?.displayName || store.getters['user/name'] || '' : '',
);
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');
const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) router.push('/main');
};
const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({ path: '/auth/login', query: { to: route.fullPath } });
  closeMypageMenu();
};
const isMenuActive = (menuLink) => {
  const p = route.path;
  if (menuLink.name === '낭만분석')
    return (
      p.startsWith('/saju-service') ||
      p.startsWith('/saju-result') ||
      p.startsWith('/saju-insight') ||
      p.startsWith('/analysis')
    );
  return p.startsWith(menuLink.basePath || menuLink.link);
};
const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') router.push(menu.link);
  closeMypageMenu();
};
const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (e) {
    console.error('Logout error:', e);
  }
};
</script>

<template>
  <div class="my-info-wrap">
    <div class="inquiry-detail-header">
      <button v-if="isWebView" class="inquiry-detail-header__back" @click="handleBack">
        <IconHeaderBack />
      </button>
      <div class="inquiry-detail-header__title">문의내역</div>
      <div v-if="!isWebView" class="inquiry-detail-header__right">
        <button class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box" @click="creditInfoRef?.closeDropdown()">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>홈
            </button>
            <template v-if="isLoggedIn"
              ><div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                ><span class="txt2">행복한 하루 되세요</span>
              </div></template
            >
            <template v-else
              ><div class="txt-box">
                <span class="txt1">게스트</span><span class="txt2">로그인 후 이용해주세요</span>
              </div></template
            >
            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />
            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</router-link
                >
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</a
                >
              </template>
            </div>
            <template v-if="isLoggedIn"
              ><MypageLnbTabMenu /><button class="btn-xs line" @click="handleLogout">
                로그아웃
              </button></template
            >
            <template v-else
              ><button class="btn-xs line login-btn" @click="handleLogin">로그인</button></template
            >
          </div>
        </div>
      </div>
    </Teleport>
    <div class="my-tit-box com-bg-my-box h-small">
      <div class="tit fnt-big">내역보기</div>
      <div class="path">
        <ul>
          <li>홈</li>
          <li>마이페이지</li>
          <li>문의/신청</li>
          <li>내역보기</li>
        </ul>
      </div>
    </div>
    <div class="myinfo-box com-bg-my-box st-inquiry">
      <div class="inquiry-notice-layer">
        <div class="inquiry-notice-layer__header">
          <div class="tit">운영 시간 안내</div>
          <div class="inquiry-notice-layer__title" @click="toggleNotice">운영 시간 안내</div>
          <div
            @click="toggleNotice"
            class="inquiry-notice-layer__chevron"
            :class="{ 'inquiry-notice-layer__chevron--expanded': isNoticeExpanded }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M15 19L8 12L15 5"
                stroke="#333333"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </div>
        <transition
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @leave="onLeave"
          @after-leave="onAfterLeave"
          :css="false"
        >
          <div v-if="isNoticeExpanded" class="inquiry-notice-layer__content">
            <div class="inquiry-notice-layer__text">
              답변시간 평일 13:00 - 18:00 (주말, 공휴일 휴무)
            </div>
            <div class="inquiry-notice-layer__text_exp">
              답변시간 이후 접수건은 운영시간 내 순차적으로 답변해드립니다.
            </div>
          </div>
        </transition>
      </div>
      <!-- 데스크톱 버전용 (768px 초과) -->
      <div class="my-exp-box desktop-only">
        <div class="time-box">
          <div class="txt1">답변시간 평일 13:00 - 18:00 (주말, 공휴일 휴무)</div>
          <div class="txt2">답변시간 이후 접수 건은 운영시간 내 순차적으로 답변해 드립니다.</div>
        </div>
        <div class="email-box">
          <div class="txt">
            <span>{{ contact.email }}</span> <IconEmail />
          </div>
          <div v-if="false" class="txt">
            <span>{{ contact.mobile }}</span
            ><IconHp />
          </div>
        </div>
      </div>
    </div>
    <div class="myinfo-box com-bg-my-box st-inquiry">
      <div class="my-tab-box">
        <button
          class="tab-button"
          :class="{ active: board === 'inquiry' }"
          @click="List('inquiry')"
        >
          1:1 문의
        </button>
        <button
          class="tab-button"
          :class="{ active: board === 'eventinquiry' }"
          @click="List('eventinquiry')"
        >
          이벤트 신청
        </button>
      </div>
      <div class="">
        <MyInquiryList :board="board" :board-id="boardId" />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.my-info-wrap .board-inquiry-box {
  display: none;
}
.my-info-wrap .board-tit-box {
  display: none;
}

.inquiry-detail-header {
  display: none;
}

.desktop-only {
  display: block;
}
.inquiry-notice-layer__chevron {
  display: none;
}
.inquiry-notice-layer__title {
  display: none;
}
.inquiry-notice-layer__header {
  .tit {
    display: block;
  }
}
@media (max-width: 768px) {
  .my-info-wrap {
    padding-top: calc(52px + var(--safe-area-top, 0px));
  }

  .inquiry-detail-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    height: calc(52px + var(--safe-area-top, 0px));
    padding-top: var(--safe-area-top, 0px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-bottom: 1px solid #ebebeb;

    .inquiry-detail-header__back {
      position: absolute;
      left: 16px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }

    .inquiry-detail-header__title {
      color: #333;
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
      text-align: center;
    }

    .inquiry-detail-header__right {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
    }

    .btn-mypage {
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      .icon-mypage {
        display: flex;
        background: url('@manseImages/icon-mypage.png') center center no-repeat;
        background-size: 32px auto;
        width: 32px;
        height: 32px;
        border-radius: 100%;
        font-size: 0;
      }
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }
  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;
      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }
        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }
      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;
        .txt1 {
          font-weight: 700;
        }
        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }
      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;
        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;
          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }
          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }
      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          background: #f5f5f5;
        }
        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);
          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }

  .my-tit-box.com-bg-my-box {
    display: none !important;
  }

  .myinfo-box.com-bg-my-box.st-inquiry {
    margin: 0;
    padding: 0;
    background: transparent;
    border-radius: 0;
    .tit {
      margin-bottom: 8px;
    }
    .tab-button {
      background-color: #fff;
    }
  }

  .desktop-only {
    display: none !important;
  }

  .inquiry-notice-layer {
    position: relative;
    background: var(--Gray-200, #f8f8f8);

    .inquiry-notice-layer__header {
      padding: 12px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .tit {
        display: none;
      }

      .inquiry-notice-layer__title {
        color: var(--Gray-800, #333);
        font-family: Pretendard;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 20px;
        display: block;
      }

      .inquiry-notice-layer__chevron {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(-90deg);
        transition: transform 0.3s ease;

        svg {
          width: 24px;
          height: 24px;
        }

        &--expanded {
          transform: rotate(90deg);
        }
      }
    }

    .inquiry-notice-layer__content {
      margin-bottom: 16px;
      padding: 0 16px 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .inquiry-notice-layer__text {
      color: var(--Gray-800, #333);
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
    }

    .inquiry-notice-layer__text_exp {
      color: var(--Gray-700, #575757);
      font-family: Pretendard;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.35px;
    }
  }
}
</style>
